import { useMemo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { easeExpo } from "@/lib/motion";

// Equirectangular projection helpers
const project = (lat, lon) => ({
  x: ((lon + 180) / 360) * 1000,
  y: ((90 - lat) / 180) * 500,
});

const CITIES = [
  { key: "usa", label: "USA", city: "New York", ...project(40.7, -74.0) },
  { key: "eu", label: "Europe", city: "Frankfurt", ...project(50.1, 8.7) },
  { key: "uae", label: "UAE", city: "Dubai", ...project(25.2, 55.3) },
  { key: "in", label: "India", city: "Mumbai", ...project(19.0, 72.8) },
  { key: "sg", label: "Singapore", city: "Singapore", ...project(1.3, 103.8) },
  { key: "hk", label: "Hong Kong", city: "Hong Kong", ...project(22.3, 114.2) },
  { key: "cn", label: "China", city: "Beijing", ...project(39.9, 116.4) },
];

// Primary trade routes per brief: USA -> UAE -> Singapore -> India -> Hong Kong
const PRIMARY_ROUTES = [
  ["usa", "uae"],
  ["uae", "sg"],
  ["sg", "in"],
  ["in", "hk"],
  ["usa", "eu"],
  ["eu", "uae"],
  ["hk", "cn"],
  ["cn", "usa"],
];

// Continent ellipses (viewBox 1000x500) approximating landmasses.
const CONTINENTS = [
  // Alaska / N. Canada
  { cx: 175, cy: 105, rx: 85, ry: 22 },
  // Continental North America
  { cx: 220, cy: 150, rx: 90, ry: 55 },
  // Greenland
  { cx: 385, cy: 95, rx: 26, ry: 26 },
  // Central America
  { cx: 240, cy: 205, rx: 30, ry: 12 },
  // South America (upper)
  { cx: 300, cy: 240, rx: 42, ry: 38 },
  // South America (lower / Andean)
  { cx: 305, cy: 320, rx: 26, ry: 60 },
  // Iceland
  { cx: 475, cy: 100, rx: 8, ry: 6 },
  // British Isles
  { cx: 485, cy: 130, rx: 12, ry: 12 },
  // Scandinavia
  { cx: 525, cy: 105, rx: 30, ry: 20 },
  // Europe main
  { cx: 520, cy: 145, rx: 55, ry: 30 },
  // Iberia
  { cx: 480, cy: 165, rx: 18, ry: 14 },
  // North Africa
  { cx: 540, cy: 210, rx: 65, ry: 26 },
  // Central Africa
  { cx: 555, cy: 260, rx: 45, ry: 34 },
  // Southern Africa
  { cx: 555, cy: 320, rx: 32, ry: 30 },
  // Madagascar
  { cx: 610, cy: 315, rx: 6, ry: 16 },
  // Middle East
  { cx: 605, cy: 195, rx: 42, ry: 22 },
  // Arabian Peninsula
  { cx: 620, cy: 220, rx: 30, ry: 25 },
  // Russia / N. Asia (broad)
  { cx: 760, cy: 115, rx: 180, ry: 32 },
  // Central Asia
  { cx: 720, cy: 165, rx: 90, ry: 26 },
  // India
  { cx: 720, cy: 230, rx: 30, ry: 38 },
  // Sri Lanka
  { cx: 735, cy: 270, rx: 5, ry: 8 },
  // SE Asia mainland
  { cx: 790, cy: 245, rx: 25, ry: 26 },
  // China / East Asia
  { cx: 830, cy: 190, rx: 55, ry: 30 },
  // Korea/Japan
  { cx: 875, cy: 170, rx: 18, ry: 24 },
  // Philippines
  { cx: 835, cy: 250, rx: 10, ry: 20 },
  // Indonesia
  { cx: 810, cy: 290, rx: 40, ry: 10 },
  // Papua New Guinea
  { cx: 875, cy: 300, rx: 18, ry: 10 },
  // Australia
  { cx: 870, cy: 345, rx: 50, ry: 25 },
  // New Zealand
  { cx: 935, cy: 385, rx: 8, ry: 12 },
];

// Deterministic pseudo-random
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateDots() {
  const rand = mulberry32(42);
  const dots = [];
  const stepX = 8;
  const stepY = 10;
  for (let x = 40; x < 970; x += stepX) {
    for (let y = 60; y < 460; y += stepY) {
      // jitter
      const jx = (rand() - 0.5) * 3.5;
      const jy = (rand() - 0.5) * 3.5;
      const px = x + jx;
      const py = y + jy;
      // Check membership
      let inside = false;
      for (const c of CONTINENTS) {
        const dx = (px - c.cx) / c.rx;
        const dy = (py - c.cy) / c.ry;
        if (dx * dx + dy * dy <= 1) {
          inside = true;
          break;
        }
      }
      if (!inside) continue;
      // Occasionally drop a dot to keep the pattern airy
      if (rand() < 0.12) continue;
      const r = 0.9 + rand() * 0.9;
      const op = 0.28 + rand() * 0.45;
      dots.push({ x: px, y: py, r, op });
    }
  }
  return dots;
}

function curveBetween(a, b, curveFactor = 0.35) {
  // Perpendicular-offset midpoint for a graceful arc that bows toward
  // the top of the map (subtracts from y).
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  const nx = -dy / dist;
  const ny = dx / dist;
  const bow = dist * curveFactor;
  // Ensure bow goes upward (negative y direction)
  const sign = ny < 0 ? 1 : -1;
  return {
    d: `M ${a.x} ${a.y} Q ${mx + nx * bow * sign} ${my + ny * bow * sign} ${b.x} ${b.y}`,
    length: dist,
  };
}

export default function WorldMap({ className = "" }) {
  const dots = useMemo(() => generateDots(), []);
  const arcs = useMemo(
    () =>
      PRIMARY_ROUTES.map(([fromKey, toKey], i) => {
        const from = CITIES.find((c) => c.key === fromKey);
        const to = CITIES.find((c) => c.key === toKey);
        const { d, length } = curveBetween(from, to);
        return { id: `arc-${i}`, d, length, from, to };
      }),
    []
  );

  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} className={`relative w-full ${className}`} data-testid="world-map">
      <svg
        viewBox="0 0 1000 500"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="cityGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#00E5FF" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="arcGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0" />
            <stop offset="30%" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#38F0FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
          </linearGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Faint equator/tropic lines */}
        <g stroke="var(--map-equator)" strokeWidth="1">
          <line x1="40" y1="250" x2="960" y2="250" strokeDasharray="2 8" />
          <line x1="40" y1="185" x2="960" y2="185" strokeDasharray="2 12" />
          <line x1="40" y1="315" x2="960" y2="315" strokeDasharray="2 12" />
        </g>

        {/* Continent dot matrix — theme-aware fill */}
        <g style={{ fill: "var(--map-dot)" }}>
          {dots.map((d, i) => (
            <circle
              key={i}
              cx={d.x}
              cy={d.y}
              r={d.r}
              fillOpacity={d.op * 0.85}
            />
          ))}
        </g>

        {/* Animated arcs */}
        <g fill="none">
          {arcs.map((a, i) => (
            <g key={a.id}>
              {/* under-glow */}
              <path
                d={a.d}
                stroke="#00E5FF"
                strokeOpacity="0.15"
                strokeWidth="2.4"
                filter="url(#softGlow)"
              />
              <motion.path
                d={a.d}
                stroke="url(#arcGrad)"
                strokeWidth="1.4"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                transition={{
                  duration: 1.6,
                  ease: easeExpo,
                  delay: 0.3 + i * 0.18,
                }}
              />
              {/* Traveling packet */}
              {inView && (
                <motion.circle
                  r="2.2"
                  fill="#FFFFFF"
                  style={{ filter: "drop-shadow(0 0 6px #00E5FF)" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0] }}
                  transition={{
                    duration: 3.2,
                    delay: 1.6 + i * 0.35,
                    repeat: Infinity,
                    repeatDelay: 1.4,
                    ease: "linear",
                  }}
                >
                  <animateMotion
                    dur="3.2s"
                    begin={`${1.6 + i * 0.35}s`}
                    repeatCount="indefinite"
                    path={a.d}
                    rotate="auto"
                    keyPoints="0;1"
                    keyTimes="0;1"
                    calcMode="linear"
                  />
                </motion.circle>
              )}
            </g>
          ))}
        </g>

        {/* City nodes */}
        <g>
          {CITIES.map((c, i) => (
            <g key={c.key} transform={`translate(${c.x} ${c.y})`}>
              <circle r="18" fill="url(#cityGlow)" />
              <motion.circle
                r="4"
                fill="none"
                stroke="#00E5FF"
                strokeWidth="1"
                strokeOpacity="0.7"
                initial={{ scale: 0.6, opacity: 0.8 }}
                animate={{ scale: [1, 3.2], opacity: [0.8, 0] }}
                transition={{
                  duration: 2.6,
                  repeat: Infinity,
                  delay: i * 0.35,
                  ease: "easeOut",
                }}
                style={{ transformOrigin: "center" }}
              />
              <circle r="2.6" fill="#FFFFFF" className="node-glow" />
              <text
                x="8"
                y="-8"
                fontSize="10"
                fontFamily="JetBrains Mono, monospace"
                style={{ fill: "var(--map-label)", textTransform: "uppercase" }}
                letterSpacing="0.14em"
              >
                {c.label}
              </text>
              <text
                x="8"
                y="4"
                fontSize="7.5"
                fontFamily="JetBrains Mono, monospace"
                style={{ fill: "var(--map-city)" }}
                fillOpacity="0.85"
              >
                {c.city}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}

export { CITIES };
